import { Dispatch } from "redux";
import { State } from "redux/store";
import { ThunkAction } from "redux-thunk";
import axios from "helpers/axiosInstance";
import {
  DatasetStatus,
  EnrichmentModel,
  EnrichmentPreview,
  EnrichmentStatus,
  ModelSelectionData,
} from "types";
import { composeApiURL } from "helpers/utility/axiosHelpers";

import { getModelSelectionData, getPreviewModels } from "./selectors";
import {
  SET_SELECTED_MODEL_ID,
  SET_ENRICHMENT_CONTEXT_ID,
  SET_ENRICHMENT_MODEL_LIST,
  SET_PREVIOUS_DATASET_ENRICHMENT_MODELS,
  SET_ENRICHMENT_PREVIEW_DATA,
  SET_USER_SELECTED_PREVIEW_MODELS,
  SET_IS_PREVIEW_LOADING,
  SET_SHOW_ENRICHMENT_PROGRESS,
  SET_ENRICHMENT_PROGRESS,
  RESET_STORE,
  SET_ENRICHED_DATASET_ID,
  SET_ENRICHMENT_STATUS,
  SET_ENRICHED_TARGET_DATASET_ID,
  SET_MODEL_SELECTION_DATA,
  SET_MODEL_PROMPT,
} from "./constants";

import { amplitudeTrack } from "helpers/utility/amplitude";
import {
  AMP_ENRICHMENT_EVENT__PREVIEW_PRESENTED,
  AMP_ENRICHMENT_EVENT__RUN_FAILED,
  AMP_ENRICHMENT_EVENT__RUN_INITIATED,
  AMP_ENRICHMENT_EVENT__PROMPT_INSERTED,
} from "helpers/constants/amplitudeEvents";
import {
  AMP_ENRICHMENT_PROPERTY__MODEL_NAME,
  AMP_ENRICHMENT_PROPERTY__MODEL_NAMES,
  AMP_ENRICHMENT_PROPERTY__NUMBER_OF_IMAGES,
  AMP_ENRICHMENT_PROPERTY__NUMBER_OF_MODELS,
  AMP_ENRICHMENT_PROPERTY__PROMPT_LENGTH,
} from "helpers/constants/amplitudeProperties";

import { getEnrichmentContextId } from "./selectors";
import { setEnrichingDataset } from "redux/Datasets/actions";

const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

export const resetStore = () => {
  return { type: RESET_STORE };
};

export const setEnrichmentPreviewLoading = (data: boolean) => {
  return { type: SET_IS_PREVIEW_LOADING, data };
};

export const setSelectedModelId = (data: string | null) => {
  return { type: SET_SELECTED_MODEL_ID, data };
};

export const setShowEnrichmentProgress = (data: boolean) => {
  return { type: SET_SHOW_ENRICHMENT_PROGRESS, data };
};

export const setEnrichmentProgress = (data: number) => {
  return { type: SET_ENRICHMENT_PROGRESS, data };
};

export const setEnrichmentStatus = (data: EnrichmentStatus) => {
  return { type: SET_ENRICHMENT_STATUS, data };
};

export const setEnrichmentContextID = (data: string | null) => {
  return { type: SET_ENRICHMENT_CONTEXT_ID, data };
};

export const setPreviousDatasetEnrichmentModels = (data: EnrichmentModel[]) => {
  return { type: SET_PREVIOUS_DATASET_ENRICHMENT_MODELS, data };
};

export const setEnrichmentModels = (data: EnrichmentModel[]) => {
  return { type: SET_ENRICHMENT_MODEL_LIST, data };
};

export const setEnrichmentPreviewData = (data: EnrichmentPreview[]) => {
  return { type: SET_ENRICHMENT_PREVIEW_DATA, data };
};

export const setUserSelectedPreviewModels = (data: EnrichmentModel[]) => {
  return { type: SET_USER_SELECTED_PREVIEW_MODELS, data };
};

export const setEnrichedDatasetId = (data: string | null) => {
  return { type: SET_ENRICHED_DATASET_ID, data };
};

export const setEnrichedTargetDatasetId = (data: string | null) => {
  return { type: SET_ENRICHED_TARGET_DATASET_ID, data };
};

export const setModelSelectionData = (data: ModelSelectionData) => {
  return { type: SET_MODEL_SELECTION_DATA, data };
};

export type ModelPromptUpdate = {
  modelId: string;
  prompt: string;
};

export const updateModelPrompt = (data: ModelPromptUpdate) => {
  return { type: SET_MODEL_PROMPT, data };
};

export const fetchEnrichmentContext = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const modelSelectionData = getModelSelectionData(state);

      const url = composeApiURL(HOST_URL, `/enrichment/${datasetId}/context`);
      const response = await axios.get(url);
      if (response.data?.status) {
        if (
          Object.values(EnrichmentStatus).includes(
            response.data?.status as EnrichmentStatus,
          )
        ) {
          dispatch(
            setEnrichmentStatus(response.data?.status as EnrichmentStatus),
          );
        }
        if (response.data?.status === EnrichmentStatus.RUNNING) {
          dispatch(setShowEnrichmentProgress(true));
          dispatch(setEnrichmentProgress(0));
        }
      }
      const enrichmentId = response.data?.enrichment_context_id || null;
      dispatch(setEnrichmentContextID(enrichmentId));
      const previousDatasetEnrichmentModels: EnrichmentModel[] = Object.values(
        response.data?.dataset_enrichment_models?.enrichment_models || {},
      );

      if (modelSelectionData?.[datasetId]) {
        let models = modelSelectionData?.[datasetId].models || [];
        let dataTypes = models.map((model) => model.type);
        let previousModels = previousDatasetEnrichmentModels.map(
          (model: any) => {
            return { ...model, replaced: dataTypes.includes(model.type) };
          },
        );

        dispatch(setPreviousDatasetEnrichmentModels(previousModels));
      } else {
        dispatch(
          setPreviousDatasetEnrichmentModels(previousDatasetEnrichmentModels),
        );
      }
    } catch (error: any) {
      console.log(error);
      dispatch(setEnrichmentContextID(null));
    }
  };
};

export const fetchEnrichmentModelList = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const url = composeApiURL(
        HOST_URL,
        `/enrichment/${datasetId}/list_models`,
      );
      const response = await axios.get(url);

      const updatedModels = response.data.map((item: any) => {
        return { ...item, selected: false };
      });

      dispatch(setEnrichmentModels(updatedModels));
    } catch (error: any) {
      console.log(error);
    }
  };
};

export const fetchEnrichmentIntialData = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(fetchEnrichmentContext(datasetId) as any);
      dispatch(fetchEnrichmentModelList(datasetId) as any);
    } catch (error: any) {
      console.log(error);
    }
  };
};

type EnrichmentModelWithMetadata = {
  [key: string]: string | { [key: string]: string };
};

const createRequestData = (
  models: EnrichmentModel[],
): Record<string, string | EnrichmentModelWithMetadata> => {
  return models.reduce(
    (acc, model) => {
      let val: string | EnrichmentModelWithMetadata = model.id;
      if (model.metadata) {
        val = {
          id: model.id,
          metadata: model.metadata,
        };
      }
      acc[model.type] = val;
      return acc;
    },
    {} as Record<string, string | EnrichmentModelWithMetadata>,
  );
};

export const fetchPreviewForSingleModel = (
  selectedModel: EnrichmentModel,
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    if (selectedModel.metadata?.prompt?.length) {
      amplitudeTrack(AMP_ENRICHMENT_EVENT__PROMPT_INSERTED, {
        [AMP_ENRICHMENT_PROPERTY__PROMPT_LENGTH]:
          selectedModel.metadata.prompt.length,
      });
    }

    const state = getState();
    const contextId = getEnrichmentContextId(state);
    const previewModels = getPreviewModels(state);

    const modelData: Record<string, string> = {};

    previewModels.forEach((model) => {
      modelData[model.type] = model.id;
    });

    modelData[selectedModel.type] = selectedModel.id;

    try {
      dispatch(setEnrichmentPreviewData([]));
      dispatch(setEnrichmentPreviewLoading(true));

      const previewModels = getPreviewModels(state);
      const combinedModels = [...previewModels, selectedModel];

      const requestBody = {
        enrichment_models: createRequestData(combinedModels),
      };

      const url = composeApiURL(
        HOST_URL,
        `/enrichment/${datasetId}/${contextId}/previews_enrichment_result`,
      );

      const response = await axios.post(url, requestBody, {
        headers: { "Content-Type": "application/json" },
      });

      const previews = response.data?.previews || [];
      dispatch(setEnrichmentPreviewData(previews));

      if (previews.length > 0) {
        amplitudeTrack(AMP_ENRICHMENT_EVENT__PREVIEW_PRESENTED, {
          [AMP_ENRICHMENT_PROPERTY__MODEL_NAME]: selectedModel.name,
        });
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      dispatch(setEnrichmentPreviewLoading(false));
    }
  };
};

export const startEnrichment = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const previewModels = getPreviewModels(state);
    const modelNames = previewModels.map((model) => model.name);
    const modelNameKey =
      modelNames.length > 1
        ? AMP_ENRICHMENT_PROPERTY__MODEL_NAMES
        : AMP_ENRICHMENT_PROPERTY__MODEL_NAME;

    try {
      const url = composeApiURL(
        HOST_URL,
        `/dataset/${datasetId}/enrich_dataset`,
      );

      const requestBody = {
        enrichment_models: createRequestData(previewModels),
      };

      const response = await axios.post(url, requestBody, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 200) {
        const numberOfImages = response.data.n_images;

        amplitudeTrack(AMP_ENRICHMENT_EVENT__RUN_INITIATED, {
          [modelNameKey]: modelNames.join(", "),
          [AMP_ENRICHMENT_PROPERTY__NUMBER_OF_MODELS]: modelNames.length,
          [AMP_ENRICHMENT_PROPERTY__NUMBER_OF_IMAGES]: numberOfImages,
        });
        dispatch(setShowEnrichmentProgress(true));
      }
    } catch (error: any) {
      console.log(error);
      amplitudeTrack(AMP_ENRICHMENT_EVENT__RUN_FAILED, {
        [modelNameKey]: modelNames.join(", "),
      });
    }
  };
};

export const abortEnrichment = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const contextId = getEnrichmentContextId(state);
      const url = composeApiURL(
        HOST_URL,
        `/enrichment/${datasetId}/${contextId}/abort`,
      );
      await axios.delete(url);
    } catch (error: any) {
      console.log(error);
    }
  };
};

export const fetchEnrichmentStatus = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const contextId = getEnrichmentContextId(state);

    try {
      const url = composeApiURL(
        HOST_URL,
        `/enrichment/${datasetId}/${contextId}/status`,
      );
      const response = await axios.get(url);
      if (response.status === 200) {
        if (response.data.status === DatasetStatus.ERROR) {
          dispatch(setEnrichmentStatus(EnrichmentStatus.FAILED));
          dispatch(setEnrichmentProgress(100));
        } else {
          if (response.data.progress) {
            dispatch(setEnrichmentProgress(response.data.progress));
          }
          if (response.data.target_dataset_id) {
            dispatch(
              setEnrichedTargetDatasetId(response.data.target_dataset_id),
            );
          }
          if (response.data.status === DatasetStatus.READY) {
            dispatch(setEnrichmentStatus(EnrichmentStatus.COMPLETED));
          }
        }
      }
    } catch (error: any) {
      console.log(error);
      dispatch(setEnrichingDataset(null));
    }
  };
};
